import { useParams, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Layout } from '@/components/ui/Layout';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/Button';
import { ChevronLeft, Loader2, ExternalLink } from 'lucide-react';
import { cn } from '@/lib/utils';

interface CampaignData {
  id: string;
  name: string;
  description: string | null;
  status: string;
  created_at: string;
}

interface CampaignSurvey {
  order_index: number;
  survey_templates: {
    id: string;
    title: string;
  };
}

interface Respondent {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  department: string | null;
  team: string | null;
}

interface InvitationRow {
  respondent_id: string;
  status: string;
  survey_respondents: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    departments: { name: string } | null;
    teams: { name: string } | null;
  };
}

const statusConfig: Record<string, { label: string; className: string }> = {
  sent: {
    label: 'Sent',
    className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-400',
  },
  completed: {
    label: 'Completed',
    className: 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-400',
  },
  expired: {
    label: 'Expired',
    className: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
  },
};

export function CampaignRespondents() {
  const { id } = useParams<{ id: string }>();
  const [campaign, setCampaign] = useState<CampaignData | null>(null);
  const [surveys, setSurveys] = useState<CampaignSurvey[]>([]);
  const [respondents, setRespondents] = useState<(Respondent & { statuses: string[] })[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) loadData();
  }, [id]);

  async function loadData() {
    try {
      setLoading(true);
      setError(null);

      const [campaignResult, surveysResult, invitationsResult] = await Promise.all([
        supabase
          .from('survey_campaigns')
          .select('id, name, description, status, created_at')
          .eq('id', id!)
          .single(),
        supabase
          .from('survey_campaign_surveys')
          .select('order_index, survey_templates(id, title)')
          .eq('campaign_id', id!)
          .order('order_index'),
        supabase
          .from('survey_invitations')
          .select(`
            respondent_id,
            status,
            survey_respondents(
              id, first_name, last_name, email,
              departments:department_id(name),
              teams:team_id(name)
            )
          `)
          .eq('campaign_id', id!),
      ]);

      if (campaignResult.error) throw campaignResult.error;
      if (surveysResult.error) throw surveysResult.error;
      if (invitationsResult.error) throw invitationsResult.error;

      setCampaign(campaignResult.data);
      setSurveys(surveysResult.data as unknown as CampaignSurvey[]);

      const invitations = invitationsResult.data as unknown as InvitationRow[];
      const respondentMap = new Map<string, Respondent & { statuses: string[] }>();

      for (const inv of invitations) {
        const r = inv.survey_respondents;
        if (!r) continue;

        if (!respondentMap.has(r.id)) {
          respondentMap.set(r.id, {
            id: r.id,
            first_name: r.first_name,
            last_name: r.last_name,
            email: r.email,
            department: r.departments?.name ?? null,
            team: r.teams?.name ?? null,
            statuses: [],
          });
        }
        respondentMap.get(r.id)!.statuses.push(inv.status);
      }

      setRespondents(
        Array.from(respondentMap.values()).sort((a, b) =>
          a.last_name.localeCompare(b.last_name)
        )
      );
    } catch (err) {
      console.error('Error loading campaign data:', err);
      setError('Failed to load campaign data');
    } finally {
      setLoading(false);
    }
  }

  function getOverallStatus(statuses: string[]): string {
    if (statuses.every(s => s === 'completed')) return 'completed';
    if (statuses.some(s => s === 'expired') && statuses.every(s => s === 'expired' || s === 'completed')) return 'expired';
    return 'sent';
  }

  if (!id) {
    return <Layout>Invalid campaign ID</Layout>;
  }

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400 dark:text-gray-500" />
        </div>
      </Layout>
    );
  }

  if (error || !campaign) {
    return (
      <Layout>
        <div className="max-w-6xl mx-auto space-y-6">
          <Link
            to="/surveys"
            className="inline-flex items-center text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
          >
            <ChevronLeft className="h-5 w-5" />
            <span className="text-sm">Back to Surveys</span>
          </Link>
          <div className="bg-red-50 dark:bg-red-900/50 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded">
            {error || 'Campaign not found'}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Back link */}
        <Link
          to="/surveys"
          className="inline-flex items-center text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
        >
          <ChevronLeft className="h-5 w-5" />
          <span className="text-sm">Back to Surveys</span>
        </Link>

        {/* Header */}
        <div>
          <h1 className="text-3xl font-semibold text-gray-900 dark:text-white">
            {campaign.name}
          </h1>
          {campaign.description && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {campaign.description}
            </p>
          )}
        </div>

        {/* Surveys in campaign */}
        {surveys.length > 0 && (
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
            <h2 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Surveys in this campaign
            </h2>
            <div className="flex flex-wrap gap-2">
              {surveys.map((s, i) => (
                <span
                  key={i}
                  className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
                >
                  {i + 1}. {(s.survey_templates as any)?.title ?? 'Unknown'}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Recipients table */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
          <div className="min-w-full overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Name
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Email
                  </th>
                  <th scope="col" className="hidden sm:table-cell px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Department
                  </th>
                  <th scope="col" className="hidden md:table-cell px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Campaign Link
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {respondents.map((respondent) => {
                  const overall = getOverallStatus(respondent.statuses);
                  const status = statusConfig[overall] ?? statusConfig.sent;
                  const campaignUrl = `${window.location.origin}/campaigns/${campaign.id}/respond/${respondent.id}`;

                  return (
                    <tr key={respondent.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {respondent.first_name} {respondent.last_name}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-[200px]">
                          {respondent.email}
                        </div>
                      </td>
                      <td className="hidden sm:table-cell px-4 py-3 whitespace-nowrap">
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {respondent.department || '-'}
                        </div>
                      </td>
                      <td className="hidden md:table-cell px-4 py-3 whitespace-nowrap">
                        <span className={cn(
                          "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium",
                          status.className
                        )}>
                          {status.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right">
                        <a
                          href={campaignUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Open campaign as this user"
                        >
                          <Button variant="outline" size="sm" type="button">
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        </a>
                      </td>
                    </tr>
                  );
                })}
                {respondents.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                      No recipients found for this campaign.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
}
